import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import { AuthContext } from '../../../context/AuthContext';
import { Button } from '../../common/Button';
import LightwayWordmark from '../../common/LightwayWordmark';

const LoginContainer = styled.div`
  max-width: 400px;
  margin: 0 auto;
  padding: ${theme.spacing.md};
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  justify-content: center;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
`;

const AppDescription = styled.p`
  font-size: ${theme.typography.fontSizes.medium};
  color: ${theme.colors.slateGray};
  text-align: center;
  line-height: 1.5;
  margin-top: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSizes.medium};
  margin-bottom: ${theme.spacing.xs};
  color: ${theme.colors.deepNavy};
`;

const Input = styled.input`
  padding: ${theme.spacing.md};
  border: 1px solid ${theme.colors.softGray};
  border-radius: ${theme.borderRadius.medium};
  font-size: ${theme.typography.fontSizes.medium};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.brandYellow};
  }
`;

const ErrorMessage = styled.p`
  color: #c62828;
  font-size: ${theme.typography.fontSizes.small};
  margin-top: ${theme.spacing.xs};
`;

const SignupLink = styled.p`
  text-align: center;
  margin-top: ${theme.spacing.lg};
  font-size: ${theme.typography.fontSizes.medium};
  color: ${theme.colors.slateGray};
`;

const Link = styled.span`
  color: blue;
  cursor: pointer;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: ${theme.spacing.lg} 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid ${theme.colors.softGray};
  }
`;

const DividerText = styled.span`
  padding: 0 ${theme.spacing.md};
  font-size: ${theme.typography.fontSizes.medium};
  color: ${theme.colors.slateGray};
`;

const GoogleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  width: 100%;
  padding: ${theme.spacing.md};
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.softGray};
  border-radius: ${theme.borderRadius.medium};
  font-size: ${theme.typography.fontSizes.medium};
  font-family: ${theme.typography.bodyFont};
  color: ${theme.colors.deepNavy};
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${theme.colors.background};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.0 24.0 0 0 0 0 21.56l7.98-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

export const LoginScreen = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login, signInWithGoogle, error } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    if (!email || !password) {
      setLoginError(t('auth.emailPasswordRequired'));
      return;
    }
    
    try {
      await login(email, password);
      navigate('/');
    } catch (error) {
      setLoginError(t('auth.invalidCredentials'));
    }
  };

  const handleGoogleSignIn = async () => {
    setLoginError('');
    setGoogleLoading(true);
    try {
      const result = await signInWithGoogle();
      if (!result) return; // User closed popup
      navigate('/');
    } catch (error) {
      if (error.code === 'auth/account-exists-with-different-credential') {
        setLoginError(t('auth.accountExistsWithDifferentCredential'));
      } else {
        setLoginError(t('auth.googleSignInError'));
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <LoginContainer>
      <Logo>
        <h1 className="lw-visually-hidden">Sign in to Lightway Advocacy</h1>
        <LightwayWordmark variant="full" iconSrc="/logo192.png" />
        <AppDescription>
          {t('auth.appDescription')}
        </AppDescription>
        <Link onClick={() => navigate('/how-it-works')} style={{ fontSize: theme.typography.fontSizes.medium, color: theme.colors.slateGray, textDecoration: 'underline' }}>How It Works →</Link>
      </Logo>

      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="email">{t('auth.email')}</Label>
          <Input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.emailPlaceholder')}
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="password">{t('auth.password')}</Label>
          <Input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('auth.passwordPlaceholder')}
          />
        </FormGroup>
        
        {(loginError || error) && (
          <ErrorMessage>{loginError || error}</ErrorMessage>
        )}
        
        <Button primary type="submit">
          {t('auth.logIn')}
        </Button>
      </Form>

      <Divider>
        <DividerText>{t('auth.or')}</DividerText>
      </Divider>

      <GoogleButton onClick={handleGoogleSignIn} disabled={googleLoading}>
        <GoogleIcon />
        {t('auth.signInWithGoogle')}
      </GoogleButton>

      <SignupLink>
        <Link onClick={() => navigate('/forgot-password')}>{t('auth.forgotPassword')}</Link>
      </SignupLink>
      
      <SignupLink>
        {t('auth.noAccount')} <Link onClick={() => navigate('/signup')}>{t('auth.signUp')}</Link>
      </SignupLink>

    </LoginContainer>
  );
};
